import React, { FunctionComponent, useEffect, useState } from "react";
import Form from "@rjsf/react-bootstrap";
import { Fields, UnlimitedWidgets } from "@webtie-ch/form-widget-library";
import { toast } from "react-toastify";
import validator from "@rjsf/validator-ajv8";

import { FormSettings as FormSettingsModel } from "../../models/userData/settings/FormSettings";
import { FormFieldsEditor } from "./FormFieldsEditor";
import { FormFieldDefinition } from "./fieldTypes";
import { buildFormMetaSchemas, waitlistMetaUiSchema } from "./FormMetaSchema";
import { WaitlistSettingsTemplate } from "./WaitlistSettingsTemplate";
import { ChevronIcon } from "../common/sidebar/icons";
import { FormDataToWebtieJsonSchemaMapper } from "./FormDataToWebtieJsonSchemaMapper";
import { WebtieJsonSchemaToFormDataMapper } from "./WebtieJsonSchemaToFormDataMapper";
import { RawFormSchema, RawFormSettings } from "../../models/userData/settings/RawFormSettings";
import FirebaseService from "../../actions/firebase/FirebaseActions";
import FormSchemaService from "../../actions/schema/FormSchemaActions";
import useSubscriptionType from "../../hooks/useSubscriptionType";
import { getFormLink } from "../../helper/getFormLink";
import { trackEvent } from "../../helper/analytics";

interface Props {
  userId: string;
  formSettings?: FormSettingsModel;
}

type SettingsTab = "general" | "registration" | "waitlist";

const SETTINGS_TABS: { id: SettingsTab; label: string }[] = [
  { id: "general", label: "Allgemein" },
  { id: "registration", label: "Anmeldung" },
  { id: "waitlist", label: "Warteliste" }
];

interface FormMeta {
  title?: string;
  releaseDate?: unknown;
  formSettings?: {
    emailConfirmationField?: string;
    maxOffersToSelect?: number;
    waitlistEnabled?: boolean;
    waitlistHint?: string;
    waitlistZipCodeMessage?: string;
    waitlistExcludedFromMaxOffers?: boolean;
  };
}

/** Doppelte oder leere Feldnamen würden Formulardaten überschreiben bzw. verlieren. */
const findFieldError = (fields: FormFieldDefinition[]): string | undefined => {
  const empty = fields.find((field) => !field.title?.trim());
  if (empty) return "Jedes Feld braucht eine Bezeichnung.";

  const titles = fields.map((field) => field.title.trim());
  const duplicate = titles.find((title, index) => titles.indexOf(title) !== index);
  return duplicate ? `Die Bezeichnung "${duplicate}" wird mehrfach verwendet.` : undefined;
};

export const FormBuilder: FunctionComponent<Props> = ({ formSettings, userId }: Props) => {
  const subscriptionType = useSubscriptionType(userId);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [fields, setFields] = useState<FormFieldDefinition[]>([]);
  const [meta, setMeta] = useState<FormMeta>({});
  const [settingsTab, setSettingsTab] = useState<SettingsTab>("general");

  const formSchema = formSettings?.formSchema;

  useEffect(() => {
    if (!formSchema?.formSchema || !formSchema?.uiSchema) return;
    const {
      title,
      releaseDate,
      formFields,
      formSettings: settings
    } = new WebtieJsonSchemaToFormDataMapper(formSchema).mapToFormData();
    setFields(formFields);
    setMeta({ title, releaseDate, formSettings: settings });
  }, [formSchema]);

  if (!formSettings) {
    return <p>Formular-Einstellungen konnten nicht geladen werden. Bitte wenden Sie sich an den Support.</p>;
  }

  if (!formSchema || !formSchema.formSchema || !formSchema.uiSchema) {
    return <p>Fehler beim Laden des Schemas</p>;
  }

  // Das Bestätigungsfeld ist eine Auswahl über die aktuell definierten Felder.
  const metaSchemas = buildFormMetaSchemas(fields);

  const save = (): void => {
    const error = findFieldError(fields);
    if (error) {
      toast.error(error);
      return;
    }

    const mapper = new FormDataToWebtieJsonSchemaMapper({ ...meta, formFields: fields });
    const jsonSchema = mapper.mapToJsonSchema();
    const uiSchema = mapper.mapToUiSchema();

    const rawFormSchema: RawFormSchema = {
      formSchema: JSON.stringify(jsonSchema),
      uiSchema: JSON.stringify(uiSchema),
      emailConfirmationField: meta.formSettings?.emailConfirmationField,
      options: {
        maxOffersToSelect: meta.formSettings?.maxOffersToSelect,
        waitlistEnabled: !!meta.formSettings?.waitlistEnabled,
        waitlistHint: meta.formSettings?.waitlistEnabled ? meta.formSettings?.waitlistHint : undefined,
        waitlistZipCodeMessage: meta.formSettings?.waitlistEnabled
          ? meta.formSettings?.waitlistZipCodeMessage
          : undefined,
        waitlistExcludedFromMaxOffers:
          !!meta.formSettings?.waitlistEnabled && !!meta.formSettings?.waitlistExcludedFromMaxOffers
      }
    };

    const rawFormSettings: RawFormSettings = {
      formSchemaEndpoint: formSettings.formSchemaEndpoint,
      formSchema: rawFormSchema
    };

    FirebaseService.updateFormSettings(userId, rawFormSettings)
      .then(() => {
        toast.success("Die Einstellungen wurden erfolgreich gespeichert.");
        const limitFields = FormSchemaService.getLimitProperties(uiSchema);
        FirebaseService.addLimitIfNotExist(userId, limitFields).catch(() =>
          toast.error(
            "Die Angebote konnten nicht automatisch erstellt werden. Bitte erstellen Sie diese manuell unter ANGEBOTE."
          )
        );
      })
      .catch((error) => toast.error(`Fehler: ${error}`))
      .finally(() => trackEvent("update-form"));
  };

  return (
    <div className="form-builder">
      <div className="form-builder__actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigator.clipboard.writeText(getFormLink(userId))}>
          Link kopieren
        </button>
        <a className="btn btn-secondary" target={"_blank"} rel={"noreferrer"} href={getFormLink(userId)}>
          Zum Formular
        </a>
        <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(!showPreview)}>
          {showPreview ? "Vorschau ausblenden" : "Vorschau"}
        </button>
        <button type="button" className="btn btn-primary" onClick={save}>
          Speichern
        </button>
      </div>

      <div className="alert alert-warning">
        Bearbeiten Sie keine Feldnamen, welche bereits Daten von Anmeldungen enthalten. Daten bestehender Anmeldungen
        können dabei verloren gehen.
      </div>

      <details className="panel accordion-panel">
        <summary className="accordion-panel__summary">
          <span className="accordion-panel__chevron" aria-hidden={true}>
            <ChevronIcon />
          </span>
          <h2>Formular-Einstellungen</h2>
          <span className="panel__meta">Titel, Veröffentlichung, Bestätigung, Warteliste</span>
        </summary>
        <div className="panel__body">
          <div className="pill-tabs mb-3" role="tablist">
            {SETTINGS_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={settingsTab === tab.id}
                className={`pill-tab${settingsTab === tab.id ? " is-active" : ""}`}
                onClick={() => setSettingsTab(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>
          {settingsTab === "general" ? (
            <Form
              key="general"
              validator={validator}
              schema={metaSchemas.general}
              formData={{ title: meta.title, releaseDate: meta.releaseDate }}
              onChange={(e: any) => setMeta((current) => ({ ...current, ...e.formData }))}
              uiSchema={{ "ui:submitButtonOptions": { norender: true } }}>
              <></>
            </Form>
          ) : (
            <Form
              key={settingsTab}
              className={settingsTab === "waitlist" ? "waitlist-settings" : undefined}
              templates={settingsTab === "waitlist" ? { ObjectFieldTemplate: WaitlistSettingsTemplate } : undefined}
              validator={validator}
              schema={metaSchemas[settingsTab]}
              formData={meta.formSettings ?? {}}
              onChange={(e: any) =>
                setMeta((current) => ({ ...current, formSettings: { ...current.formSettings, ...e.formData } }))
              }
              uiSchema={{
                ...(settingsTab === "waitlist" ? waitlistMetaUiSchema : {}),
                "ui:submitButtonOptions": { norender: true }
              }}>
              <></>
            </Form>
          )}
        </div>
      </details>

      <FormFieldsEditor fields={fields} onChange={setFields} subscriptionType={subscriptionType} />

      {showPreview && (
        <section className="panel">
          <div className="panel__head panel__head--tinted">
            <h2>Vorschau</h2>
            <span className="panel__meta">Zeigt den zuletzt gespeicherten Stand</span>
          </div>
          <div className="panel__body">
            <Form
              validator={validator}
              uiSchema={formSchema.uiSchema}
              schema={formSchema.formSchema}
              fields={Fields}
              widgets={UnlimitedWidgets}
              onSubmit={() => toast.success("Neue Anmeldung wäre eingetroffen")}>
              <button type="submit" className="btn btn-primary">
                Absenden
              </button>
            </Form>
          </div>
        </section>
      )}
    </div>
  );
};
